// Enhanced auth.jsx with proper authentication flow, error handling, and profile sync

import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from './api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// 🔥 PLATFORM ADMIN EMAILS
// Hoisted to module scope and exported so the preview gate (utils/previewFlags.jsx)
// can share ONE source of truth instead of keeping a second copy that drifts.
// Matching is case-insensitive — email case is not identity, and a login with a
// capitalized address should not silently lose admin.
export const PLATFORM_ADMIN_EMAILS = [
  'kayla@coderated.com',
  'mellow@coderated.com',        // Your working test email
  'kaydoiron1@gmail.com',     // Kayla — Colectiv user 1 (SSO handoff identity)
  'coderateddev@gmail.com',
  'divinetrickstr@gmail.com',
  'admin@coderated.com',      // Add other admin emails as needed
  'dev@coderated.com'         // Add team members
];

export const isPlatformAdmin = (email) =>
  typeof email === 'string' &&
  PLATFORM_ADMIN_EMAILS.includes(email.trim().toLowerCase());

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // CLEAN 4-TIER CONFIG - observer, professional, business, enterprise
  const TIER_CONFIG = {
    observer: {
      name: 'Observer',
      price: 0,
      limits: {
        searches: 10,
        api_calls: 0,
        analyses: 10,
        exports: 0,
        team_members: 1,
        private_analyses: 3,  // Key limitation - only 3 private analyses
        custom_reports: 0
      },
      features: [
        'browse_public_reviews',
        'basic_search',
        'limited_private_analyses',
        'basic_industry_benchmarking',
        'community_support'
      ]
    },
    professional: {
      name: 'Professional',
      price: 29,
      limits: {
        searches: -1, // unlimited
        api_calls: 1000,
        analyses: -1,
        exports: -1,
        team_members: 3,
        private_analyses: -1, // UNLIMITED private analyses
        custom_reports: 10
      },
      features: [
        'unlimited_searches',
        'unlimited_private_analyses', // Key upgrade from Observer
        'complete_scoring_breakdowns',
        'industry_benchmarking',
        'data_export_api_access',
        'email_alerts',
        'advanced_ai_insights',
        'priority_support'
      ]
    },
    business: {
      name: 'Business',
      price: 99,
      limits: {
        searches: -1,
        api_calls: 10000,
        analyses: -1,
        exports: -1,
        team_members: 10,
        private_analyses: -1,
        custom_reports: -1
      },
      features: [
        'everything_in_professional',
        'claim_manage_listings',
        'progress_tracking',
        'priority_analysis_queue',
        'white_label_reports',
        'custom_roadmaps',
        'team_collaboration',
        'enhanced_api_access'
      ]
    },
    enterprise: {
      name: 'Enterprise',
      price: 299,
      limits: {
        searches: -1,
        api_calls: -1,
        analyses: -1,
        exports: -1,
        team_members: -1,
        private_analyses: -1,
        custom_reports: -1
      },
      features: [
        'everything_in_business',
        'private_by_default',
        'custom_industry_models',
        'unlimited_api_access',
        'dedicated_account_management',
        'advanced_analytics',
        'multi_location_management',
        'custom_training',
        'sla_guarantees'
      ]
    }
    // Note: Admin is handled by email detection, not as a separate tier
    // Admin users get enterprise access + admin role
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  // Only load usage data after initialization is complete
  useEffect(() => {
    if (user && isInitialized && token) {
      const timer = setTimeout(() => {
        loadUsageData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [user, isInitialized, token]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      // Colectiv → CodeRated handoff: a short-lived token arrives in the URL
      // hash (#cst=...) from the Colectiv profile menu. Exchange it for a
      // CodeRated session before falling through to normal restore.
      const ssoMatch = window.location.hash.match(/cst=([^&]+)/);
      if (ssoMatch) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        try {
          await apiClient.initialize();
          const res = await fetch(apiClient.baseURL + '/api/auth/auth/colectiv-sso', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: decodeURIComponent(ssoMatch[1]) }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.access_token) {
              localStorage.setItem('coderated_auth_token', data.access_token);
            }
          } else {
            console.warn('Colectiv SSO exchange rejected:', res.status);
          }
        } catch (e) {
          console.warn('Colectiv SSO handoff failed:', e);
        }
      }

      const storedToken = localStorage.getItem('coderated_auth_token');
      if (!storedToken) {
        console.log('🔍 No stored token found');
        setIsInitialized(true);
        setLoading(false);
        return;
      }

      console.log('🔍 Found stored token, validating...');
      
      // Set token first
      apiClient.setAuthToken(storedToken);
      setToken(storedToken);

      // Test if token is still valid by getting user info
      try {
        const userData = await apiClient.getCurrentUser();
        setUser(userData);
        console.log('✅ User session restored:', userData);
        
        // Initialize usage with defaults
        const localUsage = JSON.parse(localStorage.getItem('coderated_usage') || '{}');
        setUsage(localUsage);
      } catch (userError) {
        console.log('❌ Token validation failed:', userError.message);
        // Clear invalid session
        localStorage.removeItem('coderated_auth_token');
        localStorage.removeItem('coderated_usage');
        apiClient.setAuthToken(null);
        setToken(null);
        setUser(null);
        setUsage({});
      }
      
    } catch (error) {
      console.log('❌ Auth initialization failed:', error.message);
      // Clear any corrupt data
      localStorage.removeItem('coderated_auth_token');
      localStorage.removeItem('coderated_usage');
      apiClient.setAuthToken(null);
      setToken(null);
      setUser(null);
      setUsage({});
    } finally {
      setIsInitialized(true);
      setLoading(false);
    }
  };

  // 🔥 ENHANCED: Refresh user data function for settings and profile sync
  const refreshUser = async () => {
    if (!token) {
      console.log('⚠️ No token available for refresh');
      return null;
    }

    try {
      console.log('🔄 Refreshing user data...');
      
      // Ensure token is set in apiClient
      apiClient.setAuthToken(token);
      
      // Get fresh user data from API
      const freshUserData = await apiClient.getCurrentUser();
      console.log('✅ Fresh user data received:', freshUserData);
      
      // 🔥 CRITICAL: Update user state immediately with detailed logging
      setUser(prevUser => {
        console.log('🔄 Updating user state from:', {
          displayName: prevUser?.displayName,
          name: prevUser?.name,
          email: prevUser?.email
        });
        console.log('🔄 Updating user state to:', {
          displayName: freshUserData?.displayName,
          name: freshUserData?.name,
          email: freshUserData?.email
        });
        return freshUserData;
      });
      
      // 🔥 CRITICAL: Also update localStorage for persistence
      localStorage.setItem('coderated_user_cache', JSON.stringify(freshUserData));
      
      // Reload usage data too
      await loadUsageData();
      
      // 🔥 CRITICAL: Force React to re-render all components using this context
      setTimeout(() => {
        console.log('🔄 Triggering forced context re-render...');
        setUser(freshUserData); // Double-set to ensure re-render
      }, 100);
      
      return freshUserData;
      
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
      
      // If refresh fails due to invalid token, logout
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        console.log('🚪 Token invalid, logging out...');
        await logout();
      }
      
      return null;
    }
  };

  // Fixed loadUsageData function
  const loadUsageData = async () => {
    if (!user || !token) {
      console.log('⚠️ Cannot load usage data - no user or token');
      return;
    }

    // Verify the apiClient has the token
    if (!apiClient.token) {
      console.log('⚠️ API Client token missing, setting it...');
      apiClient.setAuthToken(token);
    }

    try {
      console.log('📊 Loading usage data from server...');
      const usageData = await apiClient.getUserUsage(); // FIXED: Use the correct method
      setUsage(usageData);
      localStorage.setItem('coderated_usage', JSON.stringify(usageData));
      console.log('✅ Usage data loaded from server:', usageData);
    } catch (error) {
      console.warn('❌ Could not load usage data from server:', error.message);
      
      // Fall back to local storage or defaults
      const localUsage = JSON.parse(localStorage.getItem('coderated_usage') || '{}');
      
      // Get tier-appropriate defaults
      const tierConfig = getTierConfig(user?.tier);
      const defaultUsage = {
        searches: 0, // Used count, not limit
        api_calls: 0,
        analyses: 0,
        exports: 0,
        last_updated: new Date().toISOString(),
        ...localUsage
      };
      
      setUsage(defaultUsage);
      localStorage.setItem('coderated_usage', JSON.stringify(defaultUsage));
      console.log('📊 Using fallback usage data:', defaultUsage);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔐 Attempting login for:', email);
      
      // Use the updated login method from API client
      const response = await apiClient.login(email, password);
      
      if (!response.access_token) {
        throw new Error('No access token received from server');
      }
  
      console.log('✅ Login successful, token received');
      
      // Token is already set in apiClient.login(), but let's also set it in state
      setToken(response.access_token);
      
      // 🔥 UPDATED: Admin detection with enterprise access
      const userData = response.user || {
        id: 1,
        email: email,
        name: email.split('@')[0], // Use part before @ as name
        // Give admin users enterprise access, but track role separately
        tier: isPlatformAdmin(email) ? 'enterprise' : 'observer',
        role: isPlatformAdmin(email) ? 'admin' : 'user', // Admin role tracked here
        created_at: new Date().toISOString(),
        api_key: 'mock-api-key',
        analysis_count: 0
      };
      
      setUser(userData);
      console.log('✅ Using user data:', userData);
      
      // Initialize usage data
      setTimeout(() => {
        loadUsageData();
      }, 100);
      
      return userData;
      
    } catch (error) {
      console.error('❌ Login failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      // Try logout endpoint, but don't fail if it doesn't exist
      try {
        await apiClient.logout();
        console.log('✅ Server logout successful');
      } catch (error) {
        console.log('⚠️ Server logout failed (non-critical):', error.message);
      }
    } finally {
      // Always clear local state regardless of server response
      setUser(null);
      setToken(null);
      setError(null);
      setUsage({});
      setIsInitialized(false);
      localStorage.removeItem('coderated_auth_token');
      localStorage.removeItem('coderated_usage');
      localStorage.removeItem('coderated_user_cache');
      apiClient.setAuthToken(null);
      setLoading(false);
      console.log('✅ User logged out locally');
      
      // Reinitialize after logout
      setTimeout(() => {
        setIsInitialized(true);
      }, 100);
    }
  };

  const register = async (name, email, password, tier = 'observer') => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Starting registration for:', email, 'with tier:', tier);
      
      // Register the user - this returns a token immediately
      const registrationResult = await apiClient.register(name, email, password, tier);
      console.log('✅ Registration successful with token:', registrationResult);
      
      // The token is already set by apiClient.register(), just set it in state too
      setToken(registrationResult.access_token);
      
      // 🔥 UPDATED: Admin detection with enterprise access
      const userData = {
        id: registrationResult.user_id,
        email: email,
        name: name,
        // Give admin users enterprise access, but track role separately
        tier: isPlatformAdmin(email) ? 'enterprise' : (registrationResult.tier || tier),
        role: isPlatformAdmin(email) ? 'admin' : 'user', // Admin role tracked here
        created_at: new Date().toISOString(),
        api_key: registrationResult.api_key || 'mock-api-key',
        analysis_count: 0
      };
      
      setUser(userData);
      console.log('✅ Registration and auto-login successful:', userData);
      
      // Initialize usage data
      setTimeout(() => {
        loadUsageData();
      }, 100);
      
      return userData;
      
    } catch (error) {
      console.error('❌ Registration failed:', error);
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // NEW: Upgrade tier function for PricingPage
  const upgradeTier = async (newTier) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('⬆️ Upgrading user to tier:', newTier);
      
      // Validate tier
      if (!TIER_CONFIG[newTier]) {
        throw new Error('Invalid tier selected');
      }
      
      // For demo purposes, just update locally
      // In production, this would call your payment processor
      const updatedUser = {
        ...user,
        tier: newTier,
        // Keep admin role if user is admin
        role: user?.role === 'admin' ? 'admin' : 'user'
      };
      
      setUser(updatedUser);
      
      // Reset usage for new tier
      const newUsage = {
        searches: 0,
        api_calls: 0,
        analyses: 0,
        exports: 0,
        last_updated: new Date().toISOString()
      };
      
      setUsage(newUsage);
      localStorage.setItem('coderated_usage', JSON.stringify(newUsage));
      
      console.log('✅ Tier upgraded successfully to:', newTier);
      
      return updatedUser;
      
    } catch (error) {
      console.error('❌ Tier upgrade failed:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUsage = async (type, amount = 1) => {
    const newUsage = {
      ...usage,
      [type]: (usage[type] || 0) + amount,
      last_updated: new Date().toISOString()
    };
    
    setUsage(newUsage);
    localStorage.setItem('coderated_usage', JSON.stringify(newUsage));
  
    if (user && token) {
      try {
        const payload = { [type]: amount };  // ✅ fix: dynamic key
        await apiClient.updateUserUsage(payload); // FIXED: Use correct method
        console.log('✅ Usage synced to backend');
        
        // 🔥 NEW: Trigger refresh to sync with other components
        setTimeout(() => {
          refreshUser();
        }, 500);
      } catch (error) {
        console.warn('⚠️ Could not sync usage to backend:', error.message);
      }
    }
  };

  // Test token validity
  const testTokenValidity = async () => {
    if (!token) {
      console.log('⚠️ No token to validate');
      return false;
    }
    
    try {
      // Ensure token is set in apiClient
      apiClient.setAuthToken(token);
      
      // Use getCurrentUser instead of /auth/me
      const userData = await apiClient.getCurrentUser();
      console.log('✅ Token is valid, user data:', userData);
      
      // Update user data if it's different
      if (JSON.stringify(userData) !== JSON.stringify(user)) {
        setUser(userData);
      }
      
      return true;
    } catch (error) {
      console.warn('⚠️ Token validation failed:', error.message);
      return false;
    }
  };

  // Debug method
  const debugAuth = () => {
    console.log('🔍 Auth Debug Info:', {
      user,
      token: token ? `${token.substring(0, 20)}...` : null,
      loading,
      error,
      usage,
      isInitialized,
      localStorage: {
        token: localStorage.getItem('coderated_auth_token') ? 'Present' : 'Missing',
        usage: localStorage.getItem('coderated_usage') ? 'Present' : 'Missing',
        userCache: localStorage.getItem('coderated_user_cache') ? 'Present' : 'Missing'
      },
      apiClient: apiClient.getDebugInfo ? apiClient.getDebugInfo() : 'Debug method not available'
    });
  };

  // Enhanced auth checking
  const isAuthenticated = () => {
    return !!(user && token && isInitialized);
  };

  const getCurrentTier = () => {
    const userTier = user?.tier || user?.role || 'observer';
    return userTier;
  };
  
  const getTierConfig = (tier = null) => {
    const targetTier = tier || getCurrentTier();
    return TIER_CONFIG[targetTier] || TIER_CONFIG.observer;
  };

  // ENHANCED: Usage checking with admin bypass
  const checkUsageLimit = (type) => {
    const tierConfig = getTierConfig();
    const limit = tierConfig.limits[type] || 0;
    const used = usage[type] || 0;
    
    // Admin users bypass all limits
    if (user?.role === 'admin') {
      return {
        limit: -1,
        used,
        remaining: -1,
        canUse: true
      };
    }
    
    return {
      limit,
      used,
      remaining: limit === -1 ? -1 : Math.max(0, limit - used),
      canUse: limit === -1 || used < limit
    };
  };

  // ADDED: This was the missing function causing the error!
  const canPerformAction = (actionType) => {
    const usageInfo = checkUsageLimit(actionType);
    return usageInfo.canUse;
  };

  const getUpgradeRecommendation = () => {
    const currentTier = getCurrentTier();
    if (currentTier === 'observer') {
      const searchInfo = checkUsageLimit('searches');
      if (searchInfo.remaining <= 2 && searchInfo.remaining !== -1) {
        return {
          message: 'You\'re running low on searches. Upgrade to Professional for unlimited access!',
          reason: 'Low search quota'
        };
      }
      return {
        message: 'Upgrade to Professional tier for unlimited searches and advanced features.',
        reason: 'Enhanced features available'
      };
    }
    return null;
  };

  const value = {
    // Core auth
    user,
    loading,
    token,
    error,
    login,
    register,
    logout,
    clearError: () => setError(null),
    
    TIER_CONFIG,
    upgradeTier, // NEW: Add upgradeTier function
    
    // 🔥 NEW: Add refreshUser for settings and profile sync
    refreshUser,
    
    // Enhanced debugging
    debugAuth,
    testTokenValidity,
    
    // Usage tracking
    usage,
    updateUsage,
    loadUsageData,
    
    // Tier management
    getCurrentTier,
    getTierConfig,
    
    // FIXED: Dashboard compatibility methods
    checkUsageLimit,
    canPerformAction,  // ADDED: This was missing!
    getUpgradeRecommendation,
    
    // State info
    isInitialized,
    
    // Enhanced auth checking
    isAuthenticated,
    
    // Legacy compatibility with safer defaults
    userTier: getCurrentTier(),
    userId: user?.id,
    userName: user?.name,
    userEmail: user?.email
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default { useAuth, AuthProvider };